import React, { useState, useEffect } from 'react'
import { Icons, IconButton, SmartList, IHeaderItem, IColumnElement, IDropdownItem } from '@doseme/cohesive-ui'
import { isLoaded, IEntityHashMap, isLoading } from 'flux-entities'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'

import { TProps } from './types'
import { FormPageLayout } from '../../../../components/FormPageLayout'
import { IPlanHospital } from '../types'
import { IChangedSubscription } from '../../../../types/subscription'
import { TFormsValidity } from '../../types'
import { IPlan } from '../../../../types/plan'
import { SubscriptionsInputModal } from '../../../../components/SubscriptionsInputModal'
import { ILabelWithValue } from '../../../../components/SubscriptionsInputModal/types'
import { Modal } from '../../../../components/Modal/Modal'
import { HOSPITALS_URL } from '../../../../constants/urls/frontend'
import { StyledForm } from '../../../../components/StyledForm'
import { formatChangedSubscriptionRows, splitChangedSubscriptions } from '../utils'
import { ConfirmModal } from '../../../../components/ConfirmModal'
import { alphabetical } from '../../../../utils/arrayUtils'

export type TDateInputName = 'start' | 'end'
type TSubKey = 'plan' | 'start' | 'end'

const SubscriptionsEdit: React.FC<TProps> = (props) => {
  const {
    hospitals,
    plans,
    history,
    match,
    fetchHospitals,
    fetchSubscriptions,
    fetchPlans,
    updateHospital
  } = props

  const hospital = hospitals.all[match.params.id]

  const [showSubscriptionsModal, setShowSubscriptionsModal] = useState(false)
  const [idForDeleteModal, setIdForDeleteModal] = useState<string | null>(null)
  const [showExpiredPlans, setShowExpiredPlans] = useState(false)

  const [copy, updateCopy] = useState<IPlanHospital>({
    id: 0,
    instanceId: 0,
    installation: 0,
    name: '',
    maxEnabledClinicians: 0,
    subscriptions: {},
    created: moment(),
    updated: moment(),
    timezone: ''
  })
  const [hasChronologicalError, setHasChronologicalError] = useState(false)

  const [subWithPlanId, updateSubWithPlanId] = useState<string | null>(null)

  const [selectedSub, updateSelectedSub] = useState<IChangedSubscription>({
    id: null,
    planId: 0,
    start: moment(),
    end: moment().add(1, 'year'),
  })

  const [error, updateError] = useState('')

  const [subFieldsValidity, setSubFieldsValidity] = useState<TFormsValidity<TSubKey>>({
    plan: {
      touched: false,
      valid: true
    },
    start: {
      touched: true,
      valid: true,
    },
    end: {
      touched: true,
      valid: true,
    }
  })

  useEffect(() => {
    if (!hospitals.touched) { fetchHospitals() }
  }, [fetchHospitals, hospitals.touched])

  useEffect(() => {
    if (!plans.touched) { fetchPlans() }
  }, [fetchPlans, plans.touched])

  useEffect(() => {
    fetchSubscriptions(match.params.id)
  }, [fetchSubscriptions, match.params.id])

  useEffect(() => {
    const loadedHospital = hospitals.all[match.params.id]
    if (loadedHospital && props.subscriptions.all && props.subscriptions.all[match.params.id]) {
      updateCopy({
        id: loadedHospital.id,
        instanceId: loadedHospital.instanceId,
        installation: loadedHospital.installation,
        maxEnabledClinicians: loadedHospital.maxEnabledClinicians,
        name: loadedHospital.name,
        timezone: loadedHospital.timezone,
        created: loadedHospital.created,
        updated: loadedHospital.updated,
        subscriptions: props.subscriptions.all[match.params.id].reduce((subs, s) => {
          return {
            ...subs,
            [(Math.random() * 10000000).toFixed(0).toString()]: {
              id: s.id,
              planId: s.planId,
              start: s.start,
              end: s.end,
            }
          }
        }, {}),
      })
    }
  }, [hospitals, updateCopy, match.params.id, props.subscriptions.all])

  if (
    !isLoaded(hospitals) ||
    !isLoaded(plans) ||
    !isLoaded(props.subscriptions) ||
    !props.hospitals.all[props.match.params.id] ||
    !props.subscriptions.all[props.match.params.id]
  ) {
    return <div>Loading...</div>
  }

  if (!copy || !hospital) {
    return <div>No hospital was found with the id {match.params.id}</div>
  }

  const planList = plans.ids
    .reduce<ILabelWithValue[]>((acc, curr) => {
      return [
        ...acc,
        {
          label: `${plans.all[curr].name} (${plans.all[curr].type})`,
          value: plans.all[curr].id.toString()
        }
      ]
    }, [])
    .sort((x, y) => alphabetical(x.label, y.label))

  const handleDateInput = (name: TDateInputName, value: string, valid: boolean) => {
    if (
      (name === 'start' && moment(value, 'YYYY-MM-DD').isAfter(selectedSub.end)) ||
      (name === 'end' && moment(value, 'YYYY-MM-DD').isBefore(selectedSub.start))
    ) {
      setSubFieldsValidity({
        ...subFieldsValidity,
        [name]: {
          touched: true,
          valid: false
        }
      })
      setHasChronologicalError(true)
      return
    }

    if (valid) {
      updateSelectedSub({ ...selectedSub, [name]: moment(value, 'YYYY-MM-DD') })
    }

    setHasChronologicalError(false)
    setSubFieldsValidity({
      ...subFieldsValidity,
      [name]: { touched: true,
        valid
      }
    })
  }

  const handleSubPlanSelect = (item: IDropdownItem) => {
    updateSelectedSub({ ...selectedSub, planId: parseInt(item.value, 10) })

    setSubFieldsValidity({
      ...subFieldsValidity,
      plan: {
        ...subFieldsValidity.plan,
        touched: true,
      }
    })
  }

  const handleSubscriptionAdd = async () => {
    const start = selectedSub.start
    const end = selectedSub.end.clone().startOf('day').add(1, 'day').subtract(1, 'second')
    const active = moment().isBetween(start, end)
    const sub = {
      ...selectedSub,
      active,
      start,
      end
    }

    // edit or create new
    const id = subWithPlanId || (Math.random() * 100000).toFixed(0)

    const { status, message } = await updateHospital({
      ...copy,
      subscriptions: {
        ...copy.subscriptions,
        [id]: sub
      }
    })

    if (status >= 400) {
      updateError(message)
      return
    }

    // TODO: Remove this once subscriptions is completely decoupled from hospitals
    await fetchSubscriptions(match.params.id)

    updateSubWithPlanId(null)
  }

  const removeSubscription = async (id: string) => {
    const { [id]: removed, ...withoutSub } = copy.subscriptions

    const { status, message } = await updateHospital({
      ...copy,
      subscriptions: withoutSub
    })

    if (status >= 400) {
      updateError(message)
      return
    }

    // TODO: Remove this once subscriptions is completely decoupled from hospitals
    await fetchSubscriptions(match.params.id)
  }

  const subFormValid = (Object.keys(subFieldsValidity) as TSubKey[]).every(
    field => subFieldsValidity[field].valid && subFieldsValidity[field].touched)

  const cols: IHeaderItem[] = [
    {
      name: 'Plan Name',
      className: 'col-4'
    },
    {
      name: 'Start Date',
    },
    {
      name: 'Expiry Date',
    },
    {
      name: 'Expiry Date',
      displayName: '',
    }
  ]

  const columnContent = (
    idOfSubWithPlan: string,
    subscriptions: IEntityHashMap<IChangedSubscription>
  ): IColumnElement[] => {
    const sub: IChangedSubscription = subscriptions[idOfSubWithPlan]
    const plan: IPlan = plans.all[sub.planId]

    return [
      {
        name: 'planName',
        element: <span>{plan.type} - {plan.name}</span>,
        text: `${plan.type} - ${plan.name}`,
        className: 'col-4'
      },
      {
        name: 'start',
        element: <span>{sub.start.format('MMM DD, YYYY')}</span>,
        text: sub.start.format('MMM DD, YYYY'),
      },
      {
        name: 'end',
        text: sub.end.format('MMM DD, YYYY'),
        element: <div className='d-flex justify-content-between align-items-center'>
          <span className='mr-4'>{sub.end.format('MMM DD, YYYY')}</span>
        </div>
      },
      {
        name: 'actions',
        element: (
          <div>
            <Icons.EditIconButton
              className='mr-1'
              onClick={async (e) => {
                e.preventDefault()
                updateSelectedSub({
                  id: sub.id,
                  planId: plan.id,
                  start: sub.start,
                  end: sub.end,
                })
                updateSubWithPlanId(idOfSubWithPlan)
                setSubFieldsValidity({
                  ...subFieldsValidity,
                  plan: {
                    ...subFieldsValidity.plan,
                    touched: true
                  }
                })
                setShowSubscriptionsModal(true)
              }}
            />
            <Icons.DeleteIconButton
              data-testid='delete-installation'
              onClick={(e) => {
                e.preventDefault()
                setIdForDeleteModal(idOfSubWithPlan)
              }}
            />
          </div>
        )
      },
    ]
  }

  const subscriptionsModalBody = (
    <SubscriptionsInputModal
      loading={isLoading(hospitals)}
      hasChronologicalError={hasChronologicalError}
      selectedId={subWithPlanId}
      selectedSub={selectedSub}
      planList={planList}
      formValid={subFormValid}
      onPlanSelect={handleSubPlanSelect}
      handleInput={handleDateInput}
      handleCancel={() => {
        setShowSubscriptionsModal(false)
        setSubFieldsValidity({
          ...subFieldsValidity,
          plan: {
            ...subFieldsValidity.plan,
            touched: false
          }
        })
      }}
      handleSubmit={() => {
        setShowSubscriptionsModal(false)
        handleSubscriptionAdd()
        setSubFieldsValidity({
          ...subFieldsValidity,
          plan: {
            ...subFieldsValidity.plan,
            touched: false
          }
        })
      }}
    />
  )

  const { currentSubscriptions, expiredSubscriptions } = splitChangedSubscriptions(copy.subscriptions)

  const handleShowExpiredPlansToggled = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setShowExpiredPlans(evt.target.checked)
  }

  const handleDelete = async () => {
    if (idForDeleteModal) {
      await removeSubscription(idForDeleteModal)
    }
    setIdForDeleteModal(null)
  }

  const confirmDeleteModalBody = (
    <ConfirmModal
      entityType='subscription'
      cancel={() => setIdForDeleteModal(null)}
      confirm={() => handleDelete()}
    />
  )

  const confirmDeleteModal = (
    <Modal
      type='confirm'
      show={!!idForDeleteModal}
      onHide={() => setIdForDeleteModal(null)}
      body={confirmDeleteModalBody}
    />
  )

  const subscriptionsInputModal = (
    <Modal
      type='confirm'
      show={showSubscriptionsModal}
      onHide={() => {
        setShowSubscriptionsModal(false)
        setSubFieldsValidity({
          ...subFieldsValidity,
          plan: {
            ...subFieldsValidity.plan,
            touched: false
          }
        })
      }}
      body={subscriptionsModalBody}
    />
  )

  return (
    <>
      {confirmDeleteModal}
      {subscriptionsInputModal}
      <FormPageLayout
        title='Manage Subscriptions'
        testid='hospital-subscription-title'
        subtitle={`${hospital.name} – ID: ${hospital.instanceId}`}
        handleBackClick={() => history.push(HOSPITALS_URL)}
      >
        <StyledForm>
          <div>
            <div className='d-flex align-items-center pt-2 pb-3'>
              <h5 className='co-h5 pr-2'>Hospital Subscriptions</h5>
              <IconButton
                data-test='create-button'
                data-testid='create-button'
                onClick={async (e) => {
                  e.preventDefault()
                  await Promise.resolve(updateSelectedSub({
                    id: null,
                    planId: 0,
                    start: moment(),
                    end: moment().add(1, 'year'),
                  }))
                  updateSubWithPlanId(null)
                  setShowSubscriptionsModal(true)
                }}
                size='30px'
              >
                <FontAwesomeIcon icon={faPlus} />
              </IconButton>
            </div>

            <div id='subscriptions'>
              <SmartList
                cols={cols}
                data={formatChangedSubscriptionRows(currentSubscriptions, columnContent)}
                textIfEmpty='This hospital has no active subscriptions.'
              />
              {showExpiredPlans && <SmartList
                header={false}
                cols={cols}
                data={formatChangedSubscriptionRows(expiredSubscriptions, columnContent)}
              />}
            </div>
            <div className='d-flex width-100 justify-content-end align-items-center mt-2'>
              <input
                id='showExpiredPlans'
                type='checkbox'
                checked={showExpiredPlans}
                onChange={handleShowExpiredPlansToggled}
              />
              <label className='mb-0 ml-1' htmlFor='showExpiredPlans'><small>Show expired plans</small></label>
            </div>
          </div>
        </StyledForm>
        {error}
      </FormPageLayout>
    </>
  )
}

export {
  SubscriptionsEdit
}
